/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../../core/tivo/tivo-redux-wrapper';
import {
    setOverlayHighlightHorizontalLimitAction,
    setOverlayHighlightHorizontalPositionAction,
} from '../../../../../store/overlay/overlayhighlight/overlayhighlight.actions';
import {
    setPcPinAction,
    setPcPinSpinnerActiveAction,
} from '../../../../../store/overlay/pcpinoverlay/pcpinoverlay.actions';
import {
    hidePcPinOverlay,
    showIncorrectPinOverlay,
} from '../../../../../store/overlay/pcpinoverlay/pcpinoverlay.reducers';
import { PinEntryCell } from '../PinEntryCell';
import { IncorrectPinSpinner } from './IncorrectPinSpinner';

import './PinEntryWidget.scss';

interface IPinEntryWidgetProps {
    maxLength: number;
}

const PinEntryWidget: FunctionComponent<IPinEntryWidgetProps> = (props): JSX.Element => {
    const dispatch = useTivoDispatch();
    const { maxLength } = props;

    const horizontalPosition = useTivoSelector((state: RootState) => state.overlayHighlight)
        .horizontalPosition[0];
    const { pcPin } = useTivoSelector((state: RootState) => state.pcPinOverlay);
    const { spinnerActive, spinnerDelay } = useTivoSelector(
        (state: RootState) => state.pcPinOverlay,
    );
    // Mark the first cell as visited
    const [visitedCells, setVisitedCells] = useState<boolean[]>([
        true,
        ...Array(maxLength - 1).fill(false),
    ]);
    const previousHorizontalPositionRef = useRef<number>(horizontalPosition);

    useEffect(() => {
        dispatch(setOverlayHighlightHorizontalLimitAction([maxLength]));
        dispatch(setOverlayHighlightHorizontalPositionAction([0]));
        return () => {
            dispatch(setPcPinAction(''));
            dispatch(setOverlayHighlightHorizontalLimitAction([0]));
            dispatch(setOverlayHighlightHorizontalPositionAction([0]));
        };
    }, []);

    useEffect(() => {
        const previousHorizontalPosition = previousHorizontalPositionRef.current;
        const newVisitedCells = [...visitedCells];

        if (horizontalPosition !== previousHorizontalPosition) {
            if (horizontalPosition > previousHorizontalPosition) {
                newVisitedCells[horizontalPosition] = true;
            } else if (horizontalPosition < previousHorizontalPosition) {
                newVisitedCells.fill(false, horizontalPosition + 1, previousHorizontalPosition + 1);
            }
            setVisitedCells(newVisitedCells);
            previousHorizontalPositionRef.current = horizontalPosition;
        }

        return () => {
            setVisitedCells([true, ...Array(maxLength - 1).fill(false)]);
        };
    }, [horizontalPosition]);

    useEffect(() => {
        let spinnerTimmerId;
        if (spinnerActive) {
            spinnerTimmerId = setTimeout(() => {
                setVisitedCells([true, ...Array(maxLength - 1).fill(false)]);
                dispatch(setPcPinAction('0'.repeat(maxLength)));
                dispatch(setPcPinSpinnerActiveAction(false));
                dispatch(hidePcPinOverlay());
                dispatch(showIncorrectPinOverlay());
            }, spinnerDelay);
        }

        return () => {
            clearTimeout(spinnerTimmerId);
        };
    }, [spinnerActive]);

    const containerClass = `pin-entry-widget-container ${!spinnerActive ? 'pin-entry-cells' : 'icon-pc-pin-spinner-container'}`;
    const spinnerContent = <img className="icon-pc-pin-spinner" src={IncorrectPinSpinner} />;
    const pinEntryCells = [...Array(maxLength)].map((_, i) => {
        const focused = i === horizontalPosition;
        return (
            <PinEntryCell
                key={i}
                index={i}
                focused={focused}
                value={Number(pcPin[horizontalPosition]) || 0}
                visited={visitedCells[i]}
            />
        );
    });

    return <div className={containerClass}>{spinnerActive ? spinnerContent : pinEntryCells}</div>;
};

export default PinEntryWidget;
